import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import {
  DEFAULT_PRINT_PIECE_SETTINGS,
  ROLL_WIDTH_MM,
  GRAIN_LABEL_FONT_MM,
  clampLayoutPiecesToViewport,
  estimateRollLengthInches,
  getRecommendedCutLengthInches,
  getExportPieces,
  getPieceLabelPlacement,
  getPiecePathOverlays,
  getPieceCurveOverlays,
  getLayoutDiagnostics,
  getPieceConstructionGuides,
  isPrintPieceVisible,
  resolvePieceLabelText,
  resolvePieceLabelInsetInches,
  resolvePiecePrintSettings,
  PIECE_COLOR_PRESETS,
  transformPointToLayout,
} from '@/lib/pdfExport';

const PREVIEW_SCALE = 0.78;
const MOVE_STEP_MM = 5;
const POINT_TO_MM = 25.4 / 72;
const AUTO_EXPORT_PRESET_KEYS = ['composite_dark_navy', 'composite_deep_teal', 'composite_burgundy'];

const resolveExportRotationUserKey = (user = null) => String(
  user?.id
  || user?.email
  || user?.username
  || user?.first_name
  || 'default-user',
).trim();

const buildExportRotationStorageKey = (user = null) => `pattern-engine-export-rotation-${resolveExportRotationUserKey(user)}`;

const readNextAutoExportRotation = (user = null) => {
  const fallbackPresetKey = AUTO_EXPORT_PRESET_KEYS[0];
  if (typeof window === 'undefined') {
    return { nextStep: 1, presetKey: fallbackPresetKey };
  }

  const storageKey = buildExportRotationStorageKey(user);
  const lastStepRaw = Number.parseInt(window.localStorage.getItem(storageKey) || '0', 10);
  const lastStep = Number.isFinite(lastStepRaw) && lastStepRaw >= 1 && lastStepRaw <= AUTO_EXPORT_PRESET_KEYS.length
    ? lastStepRaw
    : 0;
  const nextStep = (lastStep % AUTO_EXPORT_PRESET_KEYS.length) + 1;

  return {
    nextStep,
    presetKey: AUTO_EXPORT_PRESET_KEYS[nextStep - 1],
  };
};

function PieceControlRow({
  piece,
  resolvedLabelText,
  onUpdate,
  onMove,
  onToggleFlip,
  onSelect,
  isActive,
}) {
  return (
    <div
      className={`rounded-md p-2.5 space-y-2.5 cursor-pointer ${isActive ? 'border-blue-500 ring-1 ring-blue-200 border' : ''}`}
      data-testid={`print-preview-piece-row-${piece.key}`}
      onClick={() => onSelect(piece.key)}
    >
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase">{piece.label}</div>
        <div className="flex gap-1.5">
          <Button
            type="button"
            variant={piece.flipHorizontal ? 'default' : 'outline'}
            size="sm"
            className="h-7 px-2 text-[10px]"
            onClick={() => onToggleFlip(piece.key, 'flipHorizontal')}
            data-testid={`print-preview-${piece.key}-flip-h-button`}
          >
            Flip H
          </Button>
          <Button
            type="button"
            variant={piece.flipVertical ? 'default' : 'outline'}
            size="sm"
            className="h-7 px-2 text-[10px]"
            onClick={() => onToggleFlip(piece.key, 'flipVertical')}
            data-testid={`print-preview-${piece.key}-flip-v-button`}
          >
            Flip V
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-1.5">
        <div className="space-y-1">
          <Label className="text-[10px]">X (mm)</Label>
          <Input
            type="number"
            value={piece.x}
            onChange={(e) => onUpdate(piece.key, 'x', Number(e.target.value) || 0)}
            data-testid={`print-preview-${piece.key}-x-input`}
            className="h-8 px-2 text-xs"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px]">Y (mm)</Label>
          <Input
            type="number"
            value={piece.y}
            onChange={(e) => onUpdate(piece.key, 'y', Number(e.target.value) || 0)}
            data-testid={`print-preview-${piece.key}-y-input`}
            className="h-8 px-2 text-xs"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px]">Rotation (°)</Label>
          <Input
            type="number"
            value={piece.rotation}
            onChange={(e) => onUpdate(piece.key, 'rotation', Number(e.target.value) || 0)}
            data-testid={`print-preview-${piece.key}-rotation-input`}
            className="h-8 px-2 text-xs"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px]">Label Inset (in)</Label>
          <Input
            type="number"
            step="0.5"
            min="0"
            value={piece.labelInsetInches ?? resolvePieceLabelInsetInches(piece.key)}
            onChange={(e) => onUpdate(piece.key, 'labelInsetInches', Number(e.target.value))}
            data-testid={`print-preview-${piece.key}-label-inset-input`}
            className="h-8 px-2 text-xs"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-1.5">
        <div className="space-y-1">
          <Label className="text-[10px]">Label Offset X (mm)</Label>
          <Input
            type="number"
            step="0.5"
            value={piece.labelOffsetXmm ?? 0}
            onChange={(e) => onUpdate(piece.key, 'labelOffsetXmm', Number(e.target.value) || 0)}
            data-testid={`print-preview-${piece.key}-label-offset-x-input`}
            className="h-8 px-2 text-xs"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px]">Label Offset Y (mm)</Label>
          <Input
            type="number"
            step="0.5"
            value={piece.labelOffsetYmm ?? 0}
            onChange={(e) => onUpdate(piece.key, 'labelOffsetYmm', Number(e.target.value) || 0)}
            data-testid={`print-preview-${piece.key}-label-offset-y-input`}
            className="h-8 px-2 text-xs"
          />
        </div>
      </div>

      <div className="space-y-1.5 border rounded-md p-2" data-testid={`print-preview-${piece.key}-label-settings`}>
        <div className="flex items-center justify-between">
          <Label className="text-xs">Use Global Label</Label>
          <Switch
            checked={piece.useDefaultLabel !== false}
            onCheckedChange={(checked) => onUpdate(piece.key, 'useDefaultLabel', Boolean(checked))}
            data-testid={`print-preview-${piece.key}-use-default-label-switch`}
          />
        </div>

        <div className="space-y-1">
          <Label className="text-xs">Resolved Label</Label>
          <p className="text-xs rounded border bg-muted/30 px-2 py-1" data-testid={`print-preview-${piece.key}-resolved-label-text`}>
            {resolvedLabelText || '—'}
          </p>
        </div>

        {piece.useDefaultLabel === false && (
          <div className="space-y-1">
            <Label className="text-xs">Custom Label Text</Label>
            <Input
              type="text"
              value={piece.labelTextOverride || ''}
              onChange={(e) => onUpdate(piece.key, 'labelTextOverride', e.target.value)}
              data-testid={`print-preview-${piece.key}-label-override-input`}
              className="h-8 text-xs"
            />
          </div>
        )}

        <div className="space-y-1">
          <Label className="text-xs">Font Size (pt)</Label>
          <Input
            type="number"
            step="0.5"
            min="8"
            max="120"
            value={piece.labelFontPt ?? DEFAULT_PRINT_PIECE_SETTINGS.labelFontPt}
            onChange={(e) => onUpdate(piece.key, 'labelFontPt', Number(e.target.value) || DEFAULT_PRINT_PIECE_SETTINGS.labelFontPt)}
            data-testid={`print-preview-${piece.key}-label-font-size-input`}
            className="h-8 text-xs"
          />
        </div>
      </div>

      <div className="flex items-center gap-2" data-testid={`print-preview-${piece.key}-move-handler`}>
        <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => onMove(piece.key, 0, -MOVE_STEP_MM)} data-testid={`print-preview-${piece.key}-move-up`}><ArrowUp size={13} /></Button>
        <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => onMove(piece.key, -MOVE_STEP_MM, 0)} data-testid={`print-preview-${piece.key}-move-left`}><ArrowLeft size={13} /></Button>
        <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => onMove(piece.key, MOVE_STEP_MM, 0)} data-testid={`print-preview-${piece.key}-move-right`}><ArrowRight size={13} /></Button>
        <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => onMove(piece.key, 0, MOVE_STEP_MM)} data-testid={`print-preview-${piece.key}-move-down`}><ArrowDown size={13} /></Button>
      </div>
    </div>
  );
}

export default function PrintPreviewWorkspace({
  patternData,
  currentPattern,
  measurements,
  layoutPieces,
  setLayoutPieces,
  cutLengthInches,
  setCutLengthInches,
  onCompactNesting,
  onSaveLayout,
  canSaveLayout = true,
  autosaveStatus = 'idle',
  autosaveMessage = 'Idle',
  onRetryAutosave = () => {},
  onExport,
  onExit,
}) {
  const { user } = useAuth();
  const [dragState, setDragState] = useState(null);
  const [activePieceKey, setActivePieceKey] = useState(null);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [openPieceAccordionItems, setOpenPieceAccordionItems] = useState([]);
  const [showExportColorDialog, setShowExportColorDialog] = useState(false);
  const [exportColorPreset, setExportColorPreset] = useState('auto');
  const [exportCustomCmyk, setExportCustomCmyk] = useState({ c: 0, m: 0, y: 0, k: 100 });
  const [autoExportPreview, setAutoExportPreview] = useState(() => readNextAutoExportRotation(null));
  const previewSvgRef = useRef(null);
  const canvasContainerRef = useRef(null);
  const hasAutoFittedZoomRef = useRef(false);

  const previewPatternData = useMemo(() => {
    if (!patternData) return patternData;
    return {
      ...patternData,
      measurements: {
        ...((patternData?.measurements && typeof patternData.measurements === 'object') ? patternData.measurements : {}),
        ...((measurements && typeof measurements === 'object') ? measurements : {}),
      },
    };
  }, [patternData, measurements]);

  const pieceMap = useMemo(() => {
    const pieces = getExportPieces(previewPatternData);
    return new Map(pieces.map((piece) => [piece.key, piece]));
  }, [previewPatternData]);

  const resolvedLayoutPieces = useMemo(
    () => (layoutPieces || []).map((piece) => resolvePiecePrintSettings(piece)),
    [layoutPieces],
  );
  const visibleLayoutPieces = useMemo(
    () => resolvedLayoutPieces.filter((piece) => isPrintPieceVisible(piece)),
    [resolvedLayoutPieces],
  );
  const visiblePieceCount = visibleLayoutPieces.length;

  const requiredLengthInches = useMemo(
    () => estimateRollLengthInches(patternData, layoutPieces),
    [patternData, layoutPieces],
  );

  const recommendedCutLengthInches = useMemo(
    () => getRecommendedCutLengthInches(requiredLengthInches, 0.5),
    [requiredLengthInches],
  );

  const diagnostics = useMemo(
    () => getLayoutDiagnostics(patternData, layoutPieces, cutLengthInches),
    [patternData, layoutPieces, cutLengthInches],
  );

  const warningList = diagnostics.warnings;
  const hasWarnings = warningList.length > 0;
  const effectiveCutLengthInches = diagnostics.effectiveCutLengthInches;
  const isAutoCutLength = Number(cutLengthInches) <= 0;

  const effectiveScale = PREVIEW_SCALE * previewZoom;
  const toPx = (mm) => mm * effectiveScale;

  const autosaveToneClass = autosaveStatus === 'saving'
    ? 'border-amber-200 bg-amber-50 text-amber-700'
    : autosaveStatus === 'saved'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : autosaveStatus === 'error'
        ? 'border-rose-200 bg-rose-50 text-rose-700'
        : 'border-slate-200 bg-slate-50 text-slate-700';

  const previewHeightMm = Math.max(500, (effectiveCutLengthInches || requiredLengthInches || 10) * 25.4);

  const grainFontSizePx = Math.max(14, toPx(GRAIN_LABEL_FONT_MM) + 4);

  const resolvePreviewLabelFontPx = (piece) => {
    const manualMm = (Number(piece.labelFontPt) || DEFAULT_PRINT_PIECE_SETTINGS.labelFontPt) * POINT_TO_MM;
    return Math.max(9, toPx(manualMm));
  };

  useEffect(() => {
    if (hasAutoFittedZoomRef.current) return;
    const container = canvasContainerRef.current;
    if (!container) return;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    const baseWidthPx = ROLL_WIDTH_MM * PREVIEW_SCALE;
    const baseHeightPx = previewHeightMm * PREVIEW_SCALE;
    if (containerWidth <= 0 || containerHeight <= 0 || baseWidthPx <= 0 || baseHeightPx <= 0) return;

    const fitScale = Math.min(
      1,
      (containerWidth - 24) / baseWidthPx,
      (containerHeight - 24) / baseHeightPx,
    );

    setPreviewZoom(Math.max(0.22, Number(fitScale.toFixed(2))));
    hasAutoFittedZoomRef.current = true;
  }, [previewHeightMm]);

  const applyLayoutUpdate = (updater) => {
    setLayoutPieces((prev) => {
      const next = updater(prev);
      return clampLayoutPiecesToViewport(patternData, next, cutLengthInches, 8);
    });
  };

  const updatePiece = (key, field, value, options = {}) => {
    const { setActive = true } = options;
    if (setActive) {
      setActivePieceKey(key);
    }
    const nextValue = field === 'labelInsetInches'
      ? resolvePieceLabelInsetInches(key, value)
      : value;

    applyLayoutUpdate((prev) => prev.map((piece) => (piece.key === key
      ? resolvePiecePrintSettings({ ...piece, [field]: nextValue })
      : piece)));
  };

  const movePiece = (key, dx, dy) => {
    setActivePieceKey(key);
    applyLayoutUpdate((prev) =>
      prev.map((piece) =>
        piece.key === key
          ? resolvePiecePrintSettings({
              ...piece,
              x: Number((piece.x + dx).toFixed(2)),
              y: Number((piece.y + dy).toFixed(2)),
            })
          : piece,
      ),
    );
  };

  const toggleFlip = (key, field) => {
    setActivePieceKey(key);
    applyLayoutUpdate((prev) => prev.map((piece) => (piece.key === key
      ? resolvePiecePrintSettings({ ...piece, [field]: !piece[field] })
      : piece)));
  };

  const handleMouseDown = (event, pieceKey) => {
    const svg = event.currentTarget.ownerSVGElement;
    if (!svg) return;

    const rect = svg.getBoundingClientRect();
    const mouseX = (event.clientX - rect.left) / effectiveScale;
    const mouseY = (event.clientY - rect.top) / effectiveScale;
    const piece = resolvedLayoutPieces.find((p) => p.key === pieceKey);
    if (!piece) return;

    setActivePieceKey(pieceKey);

    setDragState({
      pieceKey,
      startMouseX: mouseX,
      startMouseY: mouseY,
      originX: piece.x,
      originY: piece.y,
    });
  };

  const handleMouseMove = (event) => {
    if (!dragState) return;

    const svg = event.currentTarget;
    const rect = svg.getBoundingClientRect();
    const mouseX = (event.clientX - rect.left) / effectiveScale;
    const mouseY = (event.clientY - rect.top) / effectiveScale;

    const dx = mouseX - dragState.startMouseX;
    const dy = mouseY - dragState.startMouseY;

    applyLayoutUpdate((prev) =>
      prev.map((piece) =>
        piece.key === dragState.pieceKey
          ? { ...piece, x: Number((dragState.originX + dx).toFixed(2)), y: Number((dragState.originY + dy).toFixed(2)) }
          : piece,
      ),
    );
  };

  const clearDrag = () => setDragState(null);

  const orderedLayoutPieces = useMemo(() => {
    if (!activePieceKey) return visibleLayoutPieces;
    return [...visibleLayoutPieces].sort((a, b) => {
      if (a.key === activePieceKey) return 1;
      if (b.key === activePieceKey) return -1;
      return 0;
    });
  }, [visibleLayoutPieces, activePieceKey]);

  const handleCompactNestingClick = () => {
    const firstVisiblePiece = visibleLayoutPieces[0]?.key || 'front';
    setActivePieceKey(firstVisiblePiece);
    onCompactNesting();
  };

  const refreshAutoExportPreview = () => {
    setAutoExportPreview(readNextAutoExportRotation(user));
  };

  const handleExportModalOpen = () => {
    setExportColorPreset('auto');
    setExportCustomCmyk({ c: 0, m: 0, y: 0, k: 100 });
    refreshAutoExportPreview();
    setShowExportColorDialog(true);
  };

  const handleConfirmExport = () => {
    if (!visiblePieceCount) {
      setShowExportColorDialog(false);
      return;
    }
    let resolvedPresetKey = exportColorPreset;
    let preset = exportCustomCmyk;

    if (exportColorPreset === 'auto') {
      const nextAutoExport = readNextAutoExportRotation(user);
      resolvedPresetKey = nextAutoExport.presetKey;
      preset = PIECE_COLOR_PRESETS[resolvedPresetKey] || PIECE_COLOR_PRESETS.composite_dark_navy;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(buildExportRotationStorageKey(user), String(nextAutoExport.nextStep));
      }
      setAutoExportPreview(readNextAutoExportRotation(user));
    } else if (exportColorPreset === 'custom') {
      preset = exportCustomCmyk;
    } else {
      preset = PIECE_COLOR_PRESETS[exportColorPreset] || PIECE_COLOR_PRESETS.full_black;
    }

    setShowExportColorDialog(false);
    onExport(previewSvgRef.current, {
      colorPreset: resolvedPresetKey,
      sourceColorPreset: exportColorPreset,
      colorCmyk: {
        c: preset.c,
        m: preset.m,
        y: preset.y,
        k: preset.k,
      },
    });
  };

  useEffect(() => {
    refreshAutoExportPreview();
  }, [user]);

  return (
    <div className="flex-1 w-full flex overflow-hidden" data-testid="print-preview-mode-container">
      <div className="flex-1 border-r bg-muted/10 p-3 flex flex-col overflow-hidden">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div>
            <h2 className="text-sm font-semibold">A1 Print Preview</h2>
            <p className="text-xs text-muted-foreground" data-testid="print-preview-length-info">
              Required: {requiredLengthInches.toFixed(2)}&quot; · Cut Length: {isAutoCutLength ? `AUTO (${effectiveCutLengthInches.toFixed(2)}")` : `${Number(cutLengthInches).toFixed(2)}"`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className={`hidden md:flex items-center rounded-md border px-2 py-1 text-[11px] font-medium ${autosaveToneClass}`} data-testid="print-preview-autosave-status-badge">
              <span className="uppercase tracking-wide mr-1">Autosave:</span>
              <span data-testid="print-preview-autosave-status-message">{autosaveMessage}</span>
              {autosaveStatus === 'error' && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="ml-2 h-6 px-2 text-[10px]"
                  onClick={onRetryAutosave}
                  data-testid="print-preview-autosave-retry-button"
                >
                  Retry
                </Button>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={() => setPreviewZoom((z) => Math.max(0.22, Number((z - 0.1).toFixed(2))))} data-testid="print-preview-zoom-out-button">-</Button>
            <Button variant="outline" size="sm" onClick={() => setPreviewZoom((z) => Math.min(2.5, Number((z + 0.1).toFixed(2))))} data-testid="print-preview-zoom-in-button">+</Button>
            <Button variant="outline" size="sm" onClick={handleCompactNestingClick} disabled={visiblePieceCount <= 1} data-testid="print-preview-compact-nesting-button">Compact Nesting</Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onSaveLayout}
              disabled={!canSaveLayout}
              data-testid="print-preview-save-layout-button"
            >
              <Save size={14} /> Save
            </Button>
            <Button size="sm" onClick={handleExportModalOpen} disabled={hasWarnings || visiblePieceCount === 0} data-testid="print-preview-export-button">Export PDF</Button>
            <Button variant="ghost" size="sm" onClick={onExit} data-testid="print-preview-exit-button"><X size={14} /></Button>
          </div>
        </div>

        <div ref={canvasContainerRef} className="flex-1 border rounded-md overflow-auto bg-white" data-testid="print-preview-canvas-container">
          <svg
            ref={previewSvgRef}
            width={toPx(ROLL_WIDTH_MM)}
            height={toPx(previewHeightMm)}
            style={{ fontFamily: 'var(--font-ui)' }}
            onMouseMove={handleMouseMove}
            onMouseUp={clearDrag}
            onMouseLeave={clearDrag}
            data-testid="print-preview-canvas"
          >
            <rect x={0} y={0} width={toPx(ROLL_WIDTH_MM)} height={toPx(previewHeightMm)} fill="#f8fafc" stroke="#94a3b8" strokeWidth="1" />
            {orderedLayoutPieces.map((piece) => {
              const pieceMeta = pieceMap.get(piece.key);
              if (!pieceMeta) return null;

              const scaleX = (piece.flipHorizontal ? -1 : 1) * PREVIEW_SCALE * 25.4;
              const scaleY = (piece.flipVertical ? -1 : 1) * PREVIEW_SCALE * 25.4;

              const transform = [
                `translate(${toPx(piece.x)} ${toPx(piece.y)})`,
                `rotate(${piece.rotation})`,
                `scale(${scaleX * previewZoom} ${scaleY * previewZoom})`,
                `translate(${-pieceMeta.bounds.minX} ${-pieceMeta.bounds.minY})`,
              ].join(' ');

              const guides = getPieceConstructionGuides(previewPatternData, piece.key);
              const pathOverlays = getPiecePathOverlays(previewPatternData, piece.key);
              const pieceNotches = Array.isArray(previewPatternData?.notchMarks)
                ? previewPatternData.notchMarks.filter((mark) => mark.piece === piece.key)
                : [];
              const curveOverlays = getPieceCurveOverlays(previewPatternData, piece.key);
              const resolvedPieceLabel = resolvePieceLabelText(currentPattern, piece);

              const labelPlacement = getPieceLabelPlacement(
                previewPatternData,
                piece.key,
                piece,
                pieceMeta.bounds,
                pieceMeta.path,
              );
              const labelFontPx = resolvePreviewLabelFontPx(piece);

              return (
                <g key={`preview-piece-${piece.key}`} data-testid={`print-preview-piece-${piece.key}`}>
                  <g transform={transform}>
                    <rect
                      x={pieceMeta.bounds.minX}
                      y={pieceMeta.bounds.minY}
                      width={pieceMeta.bounds.width}
                      height={pieceMeta.bounds.height}
                      fill="transparent"
                      stroke="transparent"
                      onMouseDown={(event) => handleMouseDown(event, piece.key)}
                      style={{ cursor: 'grab' }}
                    />
                    <path
                      d={pieceMeta.path}
                      fill="none"
                      stroke={piece.key === 'front'
                        ? '#0f172a'
                        : piece.key === 'back'
                          ? '#0f172a'
                          : piece.key === 'side'
                            ? '#9a3412'
                            : piece.key === 'yoke'
                              ? '#075985'
                              : piece.key === 'collar'
                                ? '#0f766e'
                                : piece.key === 'underSleeve'
                                  ? '#0f766e'
                                  : '#6d28d9'}
                      strokeWidth={Math.max(1, toPx(0.6))}
                      vectorEffect="non-scaling-stroke"
                    />
                    {piece.key === 'back' && patternData?.dartPath && (
                      <path d={patternData.dartPath} fill="none" stroke="#7f1d1d" strokeWidth={Math.max(0.85, toPx(0.45))} vectorEffect="non-scaling-stroke" />
                    )}
                    {piece.key === 'yoke' && patternData?.yokeDartPath && (
                      <path d={patternData.yokeDartPath} fill="none" stroke="#0e7490" strokeWidth={Math.max(0.85, toPx(0.45))} vectorEffect="non-scaling-stroke" />
                    )}
                    {piece.key === 'front' && patternData?.buttonExtensionPath && (
                      <path d={patternData.buttonExtensionPath} fill="none" stroke="#0f766e" strokeWidth={Math.max(0.8, toPx(0.35))} strokeDasharray="0.2 0.14" vectorEffect="non-scaling-stroke" />
                    )}
                    {piece.key === 'front' && patternData?.placketFoldPath && (
                      <path d={patternData.placketFoldPath} fill="none" stroke="#0891b2" strokeWidth={Math.max(0.8, toPx(0.35))} strokeDasharray="0.2 0.14" vectorEffect="non-scaling-stroke" />
                    )}
                    {((piece.key === 'front' || piece.key === 'collar') && Array.isArray(patternData?.buttonholes)) && patternData.buttonholes
                      .filter((hole) => (piece.key === 'front'
                        ? (!hole.piece || hole.piece === 'front')
                        : hole.piece === 'collarLayout'))
                      .map((hole) => (
                      <line
                        key={`preview-buttonhole-${hole.id}`}
                        x1={hole.start.x}
                        y1={hole.start.y}
                        x2={hole.end.x}
                        y2={hole.end.y}
                        stroke="#111827"
                        strokeWidth={Math.max(1.05, toPx(0.5))}
                        vectorEffect="non-scaling-stroke"
                        data-testid={`print-preview-${piece.key}-buttonhole-${hole.id}`}
                      />
                    ))}
                    {((piece.key === 'front' || piece.key === 'collar') && Array.isArray(patternData?.buttons)) && patternData.buttons
                      .filter((button) => (piece.key === 'front'
                        ? (!button.piece || button.piece === 'front')
                        : button.piece === 'collarLayout'))
                      .map((button) => (
                      <g key={`preview-button-${button.id}`} data-testid={`print-preview-${piece.key}-button-${button.id}`}>
                        <circle
                          cx={button.x}
                          cy={button.y}
                          r={0.22}
                          fill="#ffffff"
                          stroke="#111827"
                          strokeWidth={Math.max(0.95, toPx(0.42))}
                          vectorEffect="non-scaling-stroke"
                        />
                        <circle
                          cx={button.x}
                          cy={button.y}
                          r={0.06}
                          fill="#111827"
                          stroke="none"
                        />
                      </g>
                    ))}
                    {piece.key === 'sleeve' && patternData?.sleevePlacketPath && (
                      <path d={patternData.sleevePlacketPath} fill="none" stroke="#6d28d9" strokeWidth={Math.max(0.9, toPx(0.45))} strokeDasharray="0.2 0.14" vectorEffect="non-scaling-stroke" />
                    )}
                    {piece.key === 'sleeve' && patternData?.sleeveLengthPath && (
                      <path d={patternData.sleeveLengthPath} fill="none" stroke="#7c3aed" strokeWidth={Math.max(0.8, toPx(0.34))} strokeDasharray="0.2 0.18" vectorEffect="non-scaling-stroke" opacity={0.85} />
                    )}
                    {pieceNotches.flatMap((mark) => (mark.paths || []).map((notchPath, index) => ({ notchPath, mark, index }))).map(({ notchPath, mark, index }) => (
                      <path
                        key={`preview-notch-${mark.id}-${index}`}
                        d={notchPath}
                        fill="none"
                        stroke="#111827"
                        strokeWidth={Math.max(1.05, toPx(0.48))}
                        vectorEffect="non-scaling-stroke"
                        opacity={0.95}
                        data-testid={`print-preview-notch-${mark.id}-${index}`}
                      />
                    ))}
                    {pathOverlays.map((overlay, index) => (
                      <path
                        key={`preview-piece-path-overlay-${piece.key}-${overlay.id || index}`}
                        d={overlay.d}
                        fill="none"
                        stroke={overlay.color || overlay.stroke || '#1f2937'}
                        strokeWidth={Math.max(0.9, toPx(Number(overlay.previewStrokeWidth ?? overlay.strokeWidth ?? 0.5)))}
                        strokeDasharray={overlay.strokeDasharray || undefined}
                        opacity={overlay.opacity ?? 1}
                        vectorEffect="non-scaling-stroke"
                        data-testid={`print-preview-piece-path-overlay-${piece.key}-${overlay.id || index}`}
                      />
                    ))}
                    {curveOverlays.map((segment, index) => (
                      <path
                        key={`preview-piece-curve-${piece.key}-${index}`}
                        d={segment.d}
                        fill="none"
                        stroke={segment.color || segment.stroke || '#1f2937'}
                        strokeWidth={Math.max(1, toPx(Number(segment.previewStrokeWidth ?? segment.strokeWidth ?? 0.5)))}
                        strokeDasharray={segment.strokeDasharray || undefined}
                        opacity={segment.opacity ?? 1}
                        vectorEffect="non-scaling-stroke"
                        data-testid={`print-preview-piece-curve-${piece.key}-${index}`}
                      />
                    ))}
                  </g>

                  {piece.key === 'front' && patternData?.pleatLines?.map((line, idx) => {
                    const p1 = transformPointToLayout({ x: line.x1, y: -line.y1 }, piece, pieceMeta.bounds);
                    const p2 = transformPointToLayout({ x: line.x2, y: -line.y2 }, piece, pieceMeta.bounds);
                    return <line key={`pleat-${idx}`} x1={toPx(p1.x)} y1={toPx(p1.y)} x2={toPx(p2.x)} y2={toPx(p2.y)} stroke="#d97706" strokeDasharray="4 3" strokeWidth={Math.max(1, toPx(0.5))} />;
                  })}

                  {piece.key === 'back' && patternData?.backPleatLines?.map((line, idx) => {
                    const p1 = transformPointToLayout({ x: line.x1, y: -line.y1 }, piece, pieceMeta.bounds);
                    const p2 = transformPointToLayout({ x: line.x2, y: -line.y2 }, piece, pieceMeta.bounds);
                    return (
                      <line
                        key={`back-pleat-${idx}`}
                        x1={toPx(p1.x)}
                        y1={toPx(p1.y)}
                        x2={toPx(p2.x)}
                        y2={toPx(p2.y)}
                        stroke={line.type === 'fold' ? '#0f172a' : '#475569'}
                        strokeDasharray={line.type === 'underlay' ? '3.4 2.4' : undefined}
                        strokeWidth={Math.max(1.4, toPx(line.type === 'fold' ? 0.6 : 0.58))}
                      />
                    );
                  })}

                  {guides.map((guide, idx) => {
                    const p1 = transformPointToLayout({ x: guide.p1.x, y: -guide.p1.y }, piece, pieceMeta.bounds);
                    const p2 = transformPointToLayout({ x: guide.p2.x, y: -guide.p2.y }, piece, pieceMeta.bounds);
                    const isCreaseGuide = guide.type === 'crease';
                    const isMeasurementGuide = guide.type === 'measurement';
                    return (
                      <g key={`guide-${piece.key}-${idx}`}>
                        <line
                          x1={toPx(p1.x)}
                          y1={toPx(p1.y)}
                          x2={toPx(p2.x)}
                          y2={toPx(p2.y)}
                          stroke={isMeasurementGuide ? '#1f2937' : (isCreaseGuide ? '#334155' : '#64748b')}
                          strokeDasharray={isMeasurementGuide ? undefined : (isCreaseGuide ? '3.2 1.8' : '3 2')}
                          strokeWidth={isMeasurementGuide ? Math.max(1, toPx(0.45)) : (isCreaseGuide ? Math.max(1.1, toPx(0.52)) : Math.max(0.9, toPx(0.4)))}
                          data-testid={`print-preview-piece-guide-${piece.key}-${guide.id || idx}`}
                        />
                      </g>
                    );
                  })}

                  {guides.filter((guide) => guide.showLabel && guide.showLabelInPreview !== false && guide.p1 && guide.p2).map((guide) => {
                    const p1 = transformPointToLayout({ x: guide.p1.x, y: -guide.p1.y }, piece, pieceMeta.bounds);
                    const p2 = transformPointToLayout({ x: guide.p2.x, y: -guide.p2.y }, piece, pieceMeta.bounds);
                    const x1 = toPx(p1.x);
                    const y1 = toPx(p1.y);
                    const x2 = toPx(p2.x);
                    const y2 = toPx(p2.y);
                    const dx = x2 - x1;
                    const dy = y2 - y1;
                    const length = Math.hypot(dx, dy) || 1;
                    const nx = -dy / length;
                    const ny = dx / length;
                    let angle = (Math.atan2(dy, dx) * 180) / Math.PI;
                    if (angle > 90 || angle < -90) angle += 180;
                    const offsetPx = toPx(Number(guide.labelOffsetMm ?? 3));
                    return (
                      <text
                        key={`guide-label-${piece.key}-${guide.id || guide.label}`}
                        x={((x1 + x2) / 2) + (nx * offsetPx)}
                        y={((y1 + y2) / 2) + (ny * offsetPx)}
                        fill="#6d28d9"
                        fontSize={Math.max(10, toPx(3.1))}
                        fontWeight="700"
                        fontFamily="Courier, 'Courier New', monospace"
                        textAnchor="middle"
                        dominantBaseline="middle"
                        transform={`rotate(${angle} ${((x1 + x2) / 2) + (nx * offsetPx)} ${((y1 + y2) / 2) + (ny * offsetPx)})`}
                        data-testid={`print-preview-piece-guide-label-${piece.key}-${guide.id || guide.label}`}
                      >
                        {guide.labelText || `${Number(guide.value || 0).toFixed(2)}"`}
                      </text>
                    );
                  })}

                  {resolvedPieceLabel && (
                    <text
                      x={toPx(labelPlacement.x)}
                      y={toPx(labelPlacement.y)}
                      fill="#111827"
                      fontSize={labelFontPx}
                      fontWeight="700"
                      fontFamily="Courier, 'Courier New', monospace"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      transform={`rotate(${labelPlacement.angle} ${toPx(labelPlacement.x)} ${toPx(labelPlacement.y)})`}
                      data-testid={`print-preview-piece-label-${piece.key}`}
                    >
                      {resolvedPieceLabel}
                    </text>
                  )}
                </g>
              );
            })}

            {visiblePieceCount === 0 && (
              <g data-testid="print-preview-empty-visible-pieces-state">
                <text
                  x={toPx(ROLL_WIDTH_MM / 2)}
                  y={toPx(90)}
                  fill="#475569"
                  fontSize={16}
                  fontWeight="600"
                  textAnchor="middle"
                >
                  No print pieces selected
                </text>
                <text
                  x={toPx(ROLL_WIDTH_MM / 2)}
                  y={toPx(104)}
                  fill="#64748b"
                  fontSize={12}
                  textAnchor="middle"
                >
                  Turn on one or more pieces in the right panel to preview and export.
                </text>
              </g>
            )}
          </svg>
        </div>
      </div>

      <aside className="h-full w-[720px] min-w-[620px] max-w-[48vw] flex-shrink-0 overflow-hidden border-l bg-card p-3" data-testid="print-preview-controls-panel">
        <div className="grid h-full min-h-0 grid-cols-[minmax(0,1.08fr)_minmax(250px,0.92fr)] gap-3" data-testid="print-preview-controls-two-column-grid">
          <section className="min-h-0 overflow-y-auto pr-1" data-testid="print-preview-piece-controls-column">
            <div className="mb-2 flex items-center justify-between gap-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-700" data-testid="print-preview-piece-controls-title">
                Pattern Piece Controls
              </h3>
              <span className="text-[10px] text-muted-foreground" data-testid="print-preview-piece-controls-count-text">
                {resolvedLayoutPieces.length} pieces
              </span>
            </div>

            <Accordion
              type="multiple"
              value={openPieceAccordionItems}
              onValueChange={setOpenPieceAccordionItems}
              className="space-y-1.5"
              data-testid="print-preview-piece-controls-accordion"
            >
              {resolvedLayoutPieces.map((piece) => {
                const resolvedLabelText = resolvePieceLabelText(currentPattern, piece);
                return (
                  <AccordionItem
                    value={`piece-${piece.key}`}
                    key={piece.key}
                    className="rounded-md border bg-white px-2"
                    data-testid={`print-preview-piece-accordion-item-${piece.key}`}
                  >
                    <AccordionTrigger className="py-2.5" data-testid={`print-preview-piece-accordion-trigger-${piece.key}`}>
                      <div className="flex min-w-0 w-full items-center justify-between gap-2 pr-2">
                        <span className="text-xs font-semibold uppercase" data-testid={`print-preview-piece-control-label-${piece.key}`}>{piece.label}</span>
                        <span className="min-w-0 flex-1 truncate text-right text-[10px] text-muted-foreground" data-testid={`print-preview-piece-label-preview-${piece.key}`}>
                          {resolvedLabelText || '—'}
                        </span>
                        {piece.visible === false && (
                          <span className="rounded-[3px] border px-1.5 py-0.5 text-[9px] uppercase text-muted-foreground" data-testid={`print-preview-piece-hidden-badge-${piece.key}`}>
                            Hidden
                          </span>
                        )}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <PieceControlRow
                        piece={piece}
                        resolvedLabelText={resolvedLabelText}
                        onUpdate={updatePiece}
                        onMove={movePiece}
                        onToggleFlip={toggleFlip}
                        onSelect={setActivePieceKey}
                        isActive={activePieceKey === piece.key}
                      />
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </section>

          <section className="min-h-0 space-y-3 overflow-y-auto border-l pl-3 pr-1" data-testid="print-preview-summary-controls-column">
            <div className="space-y-2 rounded-md border bg-white p-3" data-testid="print-preview-cut-length-panel">
              <Label className="text-xs font-semibold" data-testid="print-preview-cut-length-label">Cut Length (inches)</Label>
              <Input
                type="number"
                step="0.25"
                value={Number(cutLengthInches) > 0 ? Number(cutLengthInches) : ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '') {
                    setCutLengthInches(0);
                    return;
                  }
                  const numeric = Number(raw);
                  setCutLengthInches(Number.isFinite(numeric) && numeric > 0 ? numeric : 0);
                }}
                className="h-9"
                data-testid="print-preview-cut-length-input"
              />
              <div className="space-y-1 border-t pt-2">
                <p className="text-[11px] text-muted-foreground" data-testid="print-preview-required-minimum-text">Required minimum: {requiredLengthInches.toFixed(2)}&quot;</p>
                <p className="text-[11px] text-muted-foreground" data-testid="print-preview-recommended-cut-length-text">Recommended: {recommendedCutLengthInches.toFixed(2)}&quot;</p>
                <p className="text-[10px] leading-4 text-muted-foreground" data-testid="print-preview-auto-cut-length-hint-text">Leave blank for AUTO cut length based on current nesting.</p>
              </div>
            </div>

            <div className="space-y-2.5 rounded-md border bg-white p-3" data-testid="print-preview-piece-visibility-panel">
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-700" data-testid="print-preview-piece-visibility-title">Printable Pieces</h3>
                <p className="mt-1 text-[10px] leading-4 text-muted-foreground" data-testid="print-preview-piece-visibility-description">Choose which pieces appear in preview and PDF export.</p>
              </div>
              <div className="space-y-1.5" data-testid="print-preview-piece-visibility-list">
                {resolvedLayoutPieces.map((piece) => {
                  const isVisible = isPrintPieceVisible(piece);
                  return (
                    <div key={`visibility-${piece.key}`} className="flex items-center justify-between rounded-md border px-2.5 py-2" data-testid={`print-preview-piece-visibility-row-${piece.key}`}>
                      <div className="min-w-0">
                        <p className="truncate text-xs font-medium uppercase" data-testid={`print-preview-piece-visibility-label-${piece.key}`}>{piece.label}</p>
                        <p className="text-[10px] text-muted-foreground" data-testid={`print-preview-piece-visibility-state-${piece.key}`}>{isVisible ? 'Visible in print' : 'Hidden from print'}</p>
                      </div>
                      <Switch
                        checked={isVisible}
                        onCheckedChange={(checked) => {
                          if (!checked && activePieceKey === piece.key) {
                            setActivePieceKey(null);
                          }
                          updatePiece(piece.key, 'visible', Boolean(checked), { setActive: Boolean(checked) });
                        }}
                        data-testid={`print-preview-piece-visibility-switch-${piece.key}`}
                      />
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] font-medium text-muted-foreground" data-testid="print-preview-visible-piece-count-text">Visible pieces: {visiblePieceCount} / {resolvedLayoutPieces.length}</p>
            </div>

            {hasWarnings && (
              <div className="rounded-md border border-red-300 bg-red-50 p-3" data-testid="print-preview-border-warning-box">
                <p className="mb-1 text-xs font-semibold text-red-700" data-testid="print-preview-border-warning-title">Border warning</p>
                <ul className="list-inside list-disc space-y-1 text-xs text-red-700" data-testid="print-preview-border-warning-list">
                  {warningList.map((warning, index) => (
                    <li key={warning} data-testid={`print-preview-border-warning-${index}`}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        </div>

        <Dialog open={showExportColorDialog} onOpenChange={setShowExportColorDialog}>
          <DialogContent data-testid="print-preview-export-color-dialog">
            <DialogHeader>
              <DialogTitle data-testid="print-preview-export-color-dialog-title">Export Color Mode</DialogTitle>
            </DialogHeader>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Apply one color mode to all pattern pieces and construction marks.</Label>
              <Select value={exportColorPreset} onValueChange={setExportColorPreset}>
                <SelectTrigger data-testid="print-preview-export-color-preset-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto</SelectItem>
                  <SelectItem value="composite_dark_navy">Composite Dark Navy</SelectItem>
                  <SelectItem value="composite_deep_teal">Composite Deep Teal</SelectItem>
                  <SelectItem value="composite_burgundy">Composite Burgundy</SelectItem>
                  <SelectItem value="full_black">Full Black</SelectItem>
                  <SelectItem value="custom">Custom CMYK</SelectItem>
                </SelectContent>
              </Select>

              {exportColorPreset === 'auto' && (
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2" data-testid="print-preview-export-color-auto-info-box">
                  <p className="text-[11px] font-medium text-slate-700" data-testid="print-preview-export-color-auto-info-text">
                    Auto rotates per user: {PIECE_COLOR_PRESETS[autoExportPreview.presetKey]?.label || 'Composite Dark Navy'} on export {autoExportPreview.nextStep} of {AUTO_EXPORT_PRESET_KEYS.length}.
                  </p>
                </div>
              )}

              {exportColorPreset === 'custom' && (
                <div className="grid grid-cols-4 gap-2" data-testid="print-preview-export-color-custom-cmyk-grid">
                  {['c', 'm', 'y', 'k'].map((channel) => (
                    <div className="space-y-1" key={`export-cmyk-${channel}`}>
                      <Label className="text-xs uppercase">{channel}</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={exportCustomCmyk[channel]}
                        onChange={(event) => {
                          const nextValue = Number(event.target.value);
                          setExportCustomCmyk((prev) => ({
                            ...prev,
                            [channel]: Number.isFinite(nextValue) ? Math.max(0, Math.min(100, Math.round(nextValue))) : 0,
                          }));
                        }}
                        data-testid={`print-preview-export-color-cmyk-${channel}-input`}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowExportColorDialog(false)} data-testid="print-preview-export-color-cancel-button">Cancel</Button>
              <Button onClick={handleConfirmExport} data-testid="print-preview-export-color-confirm-button">Export</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </aside>
    </div>
  );
}
